// IndexedDB connection and schema for the Renavora client.
// Phase 3 Step 5 (2/4).
//
// DB version vs record-level schemaVersion (plan §2.5):
// - DB version: bumped when object stores / indexes change shape (DDL-like).
// - record-level `schemaVersion` (see character.ts): bumped when individual
//   row payloads change shape. These two version concepts are independent.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import type { SerializedCharacter } from './character.js';
import { runMigrations } from './migrations.js';

export const DB_NAME = 'renavora';
export const DB_VERSION = 1;

/**
 * Typed IndexedDB schema. idb's DBSchema gives us full type inference on
 * `db.get('characters', id)` etc., catching typos at typecheck time.
 */
export interface RenavoraSchema extends DBSchema {
  characters: {
    key: string;
    value: SerializedCharacter;
    indexes: {
      /**
       * Sorted access path by mtime. We mostly fetch all via `getAll()` and
       * sort in memory, but having the index leaves room for a future
       * cursor-based "load most recent N" without another DB-version bump.
       */
      byUpdatedAt: number;
    };
  };
}

export type RenavoraDb = IDBPDatabase<RenavoraSchema>;

/**
 * Module-level cached connection. Reused across CRUD calls so we don't pay
 * the openDB cost on every save/load and — more importantly — so that
 * `deleteDb` can close exactly one outstanding handle before issuing the
 * delete request (otherwise the delete blocks on the open connection).
 */
let cachedDb: Promise<RenavoraDb> | null = null;

/**
 * Open (or reuse) the Renavora IndexedDB connection.
 *
 * `upgrade` is invoked only on first open or when DB_VERSION advances. The
 * callback delegates to `runMigrations` so all DDL stays in one file.
 *
 * The cached handle also installs an `onclose` watcher to invalidate the
 * cache if the browser drops the connection (e.g. tab visibility, storage
 * pressure), so the next call re-opens cleanly.
 */
export function openDb(): Promise<RenavoraDb> {
  if (cachedDb === null) {
    cachedDb = openDB<RenavoraSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion) {
        runMigrations(db, oldVersion, newVersion);
      },
      // Triggered if another tab opens with a newer DB version — we close
      // our handle so its `upgrade` can proceed, and invalidate the cache
      // so the next openDb() call re-opens at the new version.
      blocking() {
        if (cachedDb !== null) {
          // Fire-and-forget close — the new tab is waiting on us. We don't
          // need to await our own close completing.
          void cachedDb.then((db) => {
            db.close();
          });
          cachedDb = null;
        }
      },
    });
  }
  return cachedDb;
}

/**
 * Promise-wrapped IndexedDB.deleteDatabase. Closes the cached connection
 * first so the delete request doesn't block on our own open handle.
 *
 * `onblocked` is surfaced as an explicit error so test runs catch leaked
 * DB connections held elsewhere (not our cached one) immediately instead
 * of hanging.
 */
export async function deleteDb(name: string = DB_NAME): Promise<void> {
  if (cachedDb !== null) {
    try {
      const db = await cachedDb;
      db.close();
    } catch {
      // openDb itself failed; nothing to close.
    }
    cachedDb = null;
  }
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error(`deleteDatabase error: ${name}`));
    req.onblocked = () =>
      reject(new Error(`deleteDatabase blocked: ${name} DB is open elsewhere`));
  });
}
